define('viewModels/privileged/composer/forms/formEditor/QuestionGroup',[
    'lib/knockout',
    'lib/lodash4',
    'models/composer/forms/QuestionType',
    'models/composer/forms/VisibilityCondition',
    'services/loggingService',
    'viewModels/ViewModel',
    'viewModels/privileged/composer/forms/formEditor/VisibilityConditionPicker',
    'viewModels/privileged/composer/forms/formEditor/QuestionViewFactory',
    'viewModels/privileged/composer/vmUtils',
    'viewModels/privileged/composer/EntitySubviewMixinProvider',
    'viewModels/privileged/composer/VisibilityConditionMixinProvider'
], function (
    ko,
    _,
    QuestionType,
    VisibilityConditionModel,
    loggingService,
    ViewModel,
    VisibilityConditionPicker,
    QuestionViewModelFactory,
    utils,
    EntitySubviewMixinProvider,
    VisibilityConditionMixinProvider
) {
    "use strict";

    var QuestionGroupViewModel = ViewModel.extend(function questionGroupVm_init(questionGroupModel, addQuestionGroupCallback, formEditorState, questionMap) {
        var self = this;

        self.id = questionGroupModel.id;

        self.questions = utils.subviewList(self, questionGroupModel.questions, QuestionViewModelFactory, addQuestionFromQuestion, formEditorState, questionMap); //addQuestionFromQuestion is defined below, hoisting lets me refer to it from here

        var entitySubviewMixinProvider = new EntitySubviewMixinProvider(self, "Question", questionGroupModel.questions, self.questions, {modifiedState: formEditorState.isModified, handleSelection: true});

        //self.moveQuestion(start, end);
        //self.triggerMoveQuestionUp
        //self.triggerMoveQuestionDown
        entitySubviewMixinProvider.addReorderMethods();

        //self.triggerDeleteQuestion
        //self.deleteQuestion(index)
        entitySubviewMixinProvider.addDeleteMethods();

        // Observable flag to determine if this question group has been marked as N/A
        self.naSelected = self.computed({
            read: function () {
                return questionGroupModel.naSelected();
            },
            write: function (value) {
                questionGroupModel.naSelected(value);
                formEditorState.hasScoreChanged(true);
            }
        });

        // Flag to determine if this question group is N/A-enabled
        self.isNaEnabled = formEditorState.trackModified(self, questionGroupModel.naEnabled);

        self.makeApplicable = function () {
            self.naSelected(false);
        };

        self.makeNotApplicable = function () {
            self.naSelected(true);
        };

        //Wraps questionGroupModel.name; marks modified state on write
        self.name = formEditorState.trackModified(self, questionGroupModel.name);

        //computed list of design-time validation errors with the name property
        self.nameErrors = utils.modelErrors(self, questionGroupModel.name);

        self.weight = formEditorState.trackModified(self, questionGroupModel.weight);
        self.weightErrors = utils.modelErrors(self, questionGroupModel.weight);
        self.manualWeight = formEditorState.trackModified(self, questionGroupModel.manualWeight); // keep track of whether or not the user has changed the weight manually...and don't automatically adjust it if so!
        self.setManualWeight = function() {
            self.manualWeight(true);
        };

        // format the weight as a float
        self.formattedWeight = ko.computed({
            read: function() {
                return self.weight();
            },
            write: function(value) {
                // if the user entered a blank value, reset the weight to be automatically set
                self.weight(value);
                if(self.weight() === null || self.weight() === "") {
                    self.manualWeight(false);
                } else {
                    var parsedWeight = parseFloat(value);
                    // make sure the weight entered is a number
                    if(!isNaN(parsedWeight)) {
                        self.weight(Math.round(parsedWeight*100)/100);
                    }
                }
            },
            owner: self
        }).extend({ trimmed: null });

        //whether or not questions automatically fill in default answers when the evaluation is filled out
        self.defaultAnswersToHighest = formEditorState.trackModified(self, questionGroupModel.defaultAnswersToHighest);
        self.defaultAnswersToNA = formEditorState.trackModified(self, questionGroupModel.defaultAnswersToNA);

        self.qualityQuestionGroupAnswerData = ko.observable();

        //Adds isPreviewMode, isEditMode, isEvaluatorActionMode, etc.
        _.extend(self, formEditorState.modes);

        var getVisibilityConditionQuestionOptions = self.computed(function () {
            return _.filter(questionMap(), function (question) {
                return question.questionGroupIndex() < questionGroupModel.index();
            });
        });

        self.canEnableVisibilityCondition = self.computed(function () {
            return getVisibilityConditionQuestionOptions().length > 0;
        });

        var visibilityConditionMixinProvider = new VisibilityConditionMixinProvider(self);
        visibilityConditionMixinProvider.addVisibilityConditions(questionMap, questionGroupModel, getVisibilityConditionQuestionOptions, formEditorState);

        self.validateCanMoveUp = function(questionGroupViewList) {
            var indexInBounds = (questionGroupModel.index() >= 1 &&
                                 questionGroupModel.index() <= (questionGroupViewList().length - 1));
            if (!indexInBounds) {
                return false;
            }

            // Look for conflicting predicates on questions in this group
            var questionPredicateConflicts = _.flatMap(self.questions(), function (questionView) {
                var questionPredicateViews = questionView.getPredicateViewsWithQuestionGroup(questionGroupModel.index() - 1);
                return _.map(questionPredicateViews, function (predicateView) {
                    return {
                        messageKey: 'composer.evaluations.editor.moveUpValidation.questionGroupQuestion.predicateConflict',
                        questionIndex: questionView.index()+ 1,
                        questionGroupIndex: questionView.questionGroupIndex() + 1,
                        predicateQuestionIndex: predicateView.selectedQuestion().index() + 1, // 1-based index
                        predicateQuestionGroupIndex: predicateView.selectedQuestion().questionGroupIndex() + 1
                    };
                });
            });

            // Look for conflicting predicates on the question group itself
            var questionGroupPredicateViews = self.getPredicateViewsWithQuestionGroup(questionGroupModel.index() - 1);
            var questionGroupPredicateConflicts = _.map(questionGroupPredicateViews, function(predicateView) {
                return {
                        messageKey: 'composer.evaluations.editor.moveUpValidation.questionGroup.predicateConflict',
                        questionGroupIndex: questionGroupModel.index() + 1,
                        predicateQuestionIndex: predicateView.selectedQuestion().index() + 1, // 1-based index
                        predicateQuestionGroupIndex: predicateView.selectedQuestion().questionGroupIndex() + 1
                    };
            });

            var predicateConflicts = questionGroupPredicateConflicts.concat(questionPredicateConflicts);
            if (predicateConflicts.length) {
                formEditorState.validationErrors(predicateConflicts);
            }

            return predicateConflicts.length === 0;
        };

        self.validateCanMoveDown = function(questionGroupViewList) {
            var indexInBounds = (questionGroupModel.index() >= 0 &&
                                 questionGroupModel.index() <= (questionGroupViewList().length - 2));
            if (!indexInBounds) {
                return false;
            }

            var nextQuestionGroupView = questionGroupViewList()[questionGroupModel.index() + 1];

            // Look for conflicting predicates in the next question group's list of questions
            var questionPredicateConflicts = _.flatMap(nextQuestionGroupView.questions(), function (questionView) {
                var predicateViewConflicts = questionView.getPredicateViewsWithQuestionGroup(questionGroupModel.index());
                return _.map(predicateViewConflicts, function (predicateView) {
                    return {
                        messageKey: 'composer.evaluations.editor.moveDownValidation.questionGroupQuestion.predicateConflict',
                        questionIndex: questionView.index() + 1, // 1-based index
                        questionGroupIndex: questionView.questionGroupIndex() + 1, // 1-based index
                        predicateQuestionIndex: predicateView.selectedQuestion().index() + 1,
                        predicateQuestionGroupIndex: predicateView.selectedQuestion().questionGroupIndex() + 1
                    };
                });
            });

            // Look for conflicting predicates on the next question group
            var questionGroupPredicateViews = nextQuestionGroupView.getPredicateViewsWithQuestionGroup(questionGroupModel.index());
            var questionGroupPredicateConflicts = _.map(questionGroupPredicateViews, function(predicateView) {
                return {
                        messageKey: 'composer.evaluations.editor.moveDownValidation.questionGroup.predicateConflict',
                        questionGroupIndex: questionGroupModel.index() + 1,
                        predicateQuestionIndex: predicateView.selectedQuestion().index() + 1, // 1-based index
                        predicateQuestionGroupIndex: predicateView.selectedQuestion().questionGroupIndex() + 1
                    };
            });

            var predicateConflicts = questionGroupPredicateConflicts.concat(questionPredicateConflicts);
            if (predicateConflicts.length) {
                formEditorState.validationErrors(predicateConflicts);
            }

            return predicateConflicts.length === 0;
        };

        self.visible = self.computed(function () {
            if (!self.isEditMode()) {
                // Hide if it does not pass its own visibility check, or if all of its questions are hidden
                return self.visibilityConditionVisible() && _.some(self.questions(), function (question) {
                    return question.visible();
                });
            }
            return true;
        });

        self.hasStatus = self.computed(function () {
            return self.visibilityConditionEnabled() ||
                self.isNaEnabled();
        });

        //For each question, select the default answer (if applicable)
        self.selectDefaultAnswers = function () {
            var defaultAnswerSettings = {
                highest: self.defaultAnswersToHighest(),
                na: self.defaultAnswersToNA()
            };

            _.invokeMap(self.questions(), "selectDefaultAnswer", defaultAnswerSettings);
        };

        //When isPreviewMode changes, call self.onPreviewModeChange (in a way that allows it to be overridden)
        var previewModeSubscription = self.isPreviewMode.subscribe(_.bindKey(self, "onPreviewModeChange"));

        self.onPreviewModeChange = function basePreviewModeChange(previewMode) {
            if(!previewMode) {
                self.naSelected(false);
            }
        };

        // TODO: Move selection and focused code into shared mixin or something
        var _id = questionGroupModel.modelId;
        self.isSelected = self.computed(function questionGroupVm_isSelected() {
            return formEditorState.selectedItem() === _id;
        });

        self.isFocused = self.computed(function questionGroupVm_isFocused() {
            return formEditorState.selectedItem() === _id;
        });

        self.triggerSelect = function (vm, e) {
            var evt = e.originalEvent || e; //originalEvent, if we've hit a jQuery callback at some point
            if(!evt.ccPreventDeselect) {
                self.select();
                evt.ccPreventDeselect = true;
            }
            // NOTE: This must be here to allow checkboxes (etc) to be checked
            return true;
        };

        // Selects this question in the form editor
        self.select = function () {
            if (self.isEditMode()) {
                formEditorState.selectedItem(_id);
            }
        };

        self.unselect = function () {
            if (self.isEditMode()) {
                formEditorState.selectedItem(null);
            }
        };

        //Methods for adding sibling question groups using the callback passed to the viewmodel when constructed
        self.addQuestionGroupAbove = function () {
            addQuestionGroupCallback({}, self, addQuestionGroupCallback.ABOVE);
        };
        self.addQuestionGroupBelow = function () {
            addQuestionGroupCallback({}, self, addQuestionGroupCallback.BELOW);
        };


        /*jslint latedef:false*/ //I really don't want to put this logic before the subview list logic;

        //Adds a question from an existing question; takes the data of the question to add, the viewModel triggering the add, and the desired direction to add it in.
        function addQuestionFromQuestion(data, addingQuestionViewModel, direction) {
            var questionIndex = _.indexOf(self.questions(), addingQuestionViewModel);

            if(questionIndex === -1) {
                loggingService.error("Couldn't find index of addingQuestionViewModel in question group");
                return;
            }
            if(direction !== addQuestionFromQuestion.ABOVE && direction !== addQuestionFromQuestion.BELOW) {
                loggingService.error("addQuestionCallback called with invalid direction: "+direction);
                return;
            }

            var newIndex = (direction === addQuestionFromQuestion.ABOVE ? questionIndex : questionIndex + 1);

            self.addQuestion(data, newIndex);

        }
        addQuestionFromQuestion.ABOVE = "Above";
        addQuestionFromQuestion.BELOW = "Below";

        self.addQuestion = function (data, newIndex) {
            newIndex = newIndex || 0;

            questionGroupModel.addQuestion(data, newIndex);
            //Update the form editor state that the evaluation form has been modified
            formEditorState.isModified(true);
        };

        //computed that controls the hoverable css class for hover behavior
        self.canHover = self.computed(function () {
            return !formEditorState.selectedItem();
        });

        //Attached to keydown event to prevent enter key on contenteditable
        self.preventEnterKey = utils.preventEnterKey;

        // Get the answers in a form that quality can use them.  Transform attribute names
        self.getQualityAnswerData = function() {
            // separate the evaluation form (questions) from the answers
            // iterate over question groups
            var questionScores = _.map(self.questions(), function (question) {
                return question.getQualityAnswerData();
            });

            return {
                questionGroupId: questionGroupModel.id,
                markedNA: questionGroupModel.naSelected(),
                questionScores: questionScores
            };

        };

        //Fills in question group answer values from the quality service with provided answer data
        self.fillQualityAnswerData = function(qualityQuestionGroupAnswerData) {
            if (qualityQuestionGroupAnswerData) {
                self.qualityQuestionGroupAnswerData(_.pick(qualityQuestionGroupAnswerData,
                                                           ["totalScoreUnweighted", "maxTotalScoreUnweighted",
                                                            "totalCriticalScoreUnweighted", "maxTotalCriticalScoreUnweighted"]));
                // Back-fill the group name so it will show up in the scoring display
                qualityQuestionGroupAnswerData.groupName = self.name();
                if (qualityQuestionGroupAnswerData.markedNA) {
                    if (!self.isNaEnabled()) {
                        loggingService.error("Attempted to select NA for a question group that isn't NA enabled");
                    } else {
                        self.naSelected(true);
                    }
                }
                _.each(qualityQuestionGroupAnswerData.questionScores, function (qualityQuestionAnswerData) {
                    var id = qualityQuestionAnswerData.questionId;
                    //Find the corresponding question
                    var questionViewModel = _.find(self.questions(), {id: id});
                    if (!questionViewModel) {
                        loggingService.error("Provided answer data for a question that doesn't exist", { id: id });
                        return;
                    }
                    questionViewModel.fillQualityAnswerData(qualityQuestionAnswerData);
                });
            }
        };

        // Returns the scores to be displayed during evaluation review
        self.scoreReviewValue = function () {
            var answerData = self.qualityQuestionGroupAnswerData();
            var result;
            if (answerData) {
                result = ko.i18n.renderString('composer.evaluations.scoring.groupScore.scoreValue',
                             { score: answerData.totalScoreUnweighted.toFixed(0),
                               maxScore: answerData.maxTotalScoreUnweighted.toFixed(0)
                             });
            } else {
                result = ko.i18n.renderString('composer.evaluations.scoring.groupScoreNoValue');
            }
            return result;
        };

        self.criticalScoreReturnValue = function() {
            var answerData = self.qualityQuestionGroupAnswerData();
            var criticalResult;
            if (answerData) {
                criticalResult = ko.i18n.renderString('composer.evaluations.scoring.groupScore.criticalValue',
                    {
                        critScore: answerData.totalCriticalScoreUnweighted.toFixed(0),
                        maxCritScore: answerData.maxTotalCriticalScoreUnweighted.toFixed(0)
                    });
            } else {
                criticalResult = ko.i18n.renderString('composer.evaluations.scoring.groupScoreNoValue');
            }
            return criticalResult;
        };

        // disposes subscription to isPreviewMode
        self.onDispose = function () {
            previewModeSubscription.dispose();
        };

    });

    return QuestionGroupViewModel;
});

